'use client';

import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import { T } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import PlusIcon from '@flipster/react/icon/generated/PlusIcon';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { center, circle, grid, hstack, square, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { useAuth } from '../../shared/auth/state';
import { GlassCard } from '../shared/glass-card';
import { VipButton } from '../shared/vip-button';
import { FileValidationError, vipProgramPageBunja } from '../state';

export const sectionId = 'vip-apply-form';

interface Props {
  css?: Styles;
}

export const ApplyFormSection = ({ css: cssProps }: Props) => {
  const t = useTranslations();
  const router = useRouter();
  const {
    telegramUsernameAtom,
    uploadedFilesAtom,
    isAgreedAtom,
    isSubmittingAtom,
    canUploadMoreAtom,
    usernameErrorAtom,
    usernameTooLongErrorAtom,
    uploadErrorAtom,
    consentErrorAtom,
    validateAndAddFiles,
    removeFile,
    submitApplication,
    maxFileCount,
    maxFileSizeInMB,
  } = useBunja(vipProgramPageBunja);

  const [telegramUsername, setTelegramUsername] = useAtom(telegramUsernameAtom);
  const uploadedFiles = useAtomValue(uploadedFilesAtom);
  const [isAgreed, setIsAgreed] = useAtom(isAgreedAtom);
  const isSubmitting = useAtomValue(isSubmittingAtom);
  const canUploadMore = useAtomValue(canUploadMoreAtom);
  const showUsernameError = useAtomValue(usernameErrorAtom);
  const showUsernameTooLongError = useAtomValue(usernameTooLongErrorAtom);
  const showUploadError = useAtomValue(uploadErrorAtom);
  const showConsentError = useAtomValue(consentErrorAtom);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isLoggedIn } = useAuth();

  const openLoginDialog = () => {
    dialog.open({
      title: { label: <T id='Vip.r260121.Log_in_to_apply' /> },
      content: <T id='Vip.r260121.Log_in_to_submit_your_VIP_upgrade_application' />,
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={<T id='Common.r250918.Got_it' />}
            onClick={() => {
              dialog.close();
              router.push('/login');
            }}
          />
        ),
      },
    });
  };

  const showError = (error: FileValidationError) => {
    const errorMessages: Record<FileValidationError, React.ReactNode> = {
      max_files_exceeded: <T id='Vip.r260121.You_can_upload_up_to_count_files' values={{ count: maxFileCount }} />,
      invalid_file_type: <T id='Vip.r260121.This_image_format_is_not_supported' />,
      file_too_large: (
        <T id='Vip.r260121.The_file_size_must_be_smaller_than_size_mb' values={{ size: maxFileSizeInMB }} />
      ),
    };
    snackbar.error({ message: errorMessages[error] });
  };

  const handleFiles = (files: FileList) => {
    const error = validateAndAddFiles(files);
    if (error) showError(error);
  };

  return (
    <section
      id={sectionId}
      className={css(
        center.raw({
          flexDir: 'column',
          gap: { base: 40, tablet: 64 },
          py: 40,
          px: 16,
        }),
        cssProps,
      )}
    >
      <div className={vstack({ gap: 8, textAlign: 'center' })}>
        <p className={css({ textStyle: 'Title/S', color: 'rgba(255, 208, 132)' })}>
          <T id='VIP.r260303.Apply_now' />
        </p>
        <h2 className={css({ textStyle: 'Display/XL', color: 'content.default.level-5' })}>
          <T
            id='Vip.r260303.Apply_for_vip_1'
            values={{
              vip: (chunks: React.ReactNode) => (
                <span className={css({ color: 'rgba(255, 208, 132)', fontWeight: 'bold' })}>{chunks}</span>
              ),
            }}
          />
        </h2>
      </div>
      <div className={vstack({ gap: 24, w: '100%' })}>
        <GlassCard css={{ gap: 24, p: { base: 24, tablet: 40 }, w: '100%' }}>
          <div className={stack({ gap: 12 })}>
            <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
              <T id='Vip.r260121.Telegram_username' />
              <span className={css({ color: 'content.accent.new' })}> *</span>
            </p>
            <div
              className={hstack({
                py: 13,
                px: 12,
                borderColor: 'border.default.level-3',
                borderWidth: '1px',
                borderStyle: 'solid',
                rounded: 4,
                gap: 4,
              })}
            >
              <input
                type='text'
                placeholder={t('Vip.r260121.Enter_username')}
                value={telegramUsername}
                onChange={(e) => setTelegramUsername(e.target.value)}
                onFocus={() => {
                  if (!isLoggedIn) openLoginDialog();
                }}
                className={css({
                  textStyle: 'Text/L/Regular',
                  color: 'content.default.level-5',
                  bg: 'transparent',
                  border: 'none',
                  outline: 'none',
                  w: '100%',
                  _placeholder: {
                    color: 'content.default.level-1',
                  },
                })}
              />
            </div>
            {showUsernameError && (
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.accent.new' })}>
                <T id='Vip.r260121.Enter_username_dot' />
              </p>
            )}
            {showUsernameTooLongError && (
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.accent.new' })}>
                <T id='Copy.r260209.Enter_a_username_up_to_32_characters' />
              </p>
            )}
          </div>

          <div className={stack({ gap: 16 })}>
            <div className={stack({ gap: 4 })}>
              <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
                <T id='Vip.r260121.Upload_at_least_one_proof' />
                <span className={css({ color: 'content.accent.new' })}> *</span>
              </p>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                <T
                  id='Vip.r260121.VIP_status_on_another_exchange_or_days_day_trading_volume_assets'
                  values={{ days: 30 }}
                />
              </p>
            </div>
            <div className={stack({ gap: 8 })}>
              <input
                ref={fileInputRef}
                type='file'
                accept='.png,.jpg,.jpeg'
                multiple
                onClick={(e) => {
                  if (!canUploadMore) {
                    snackbar.error({ message: `You can upload up to ${maxFileCount} files.` });
                    return e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  if (e.target.files) {
                    handleFiles(e.target.files);
                    e.target.value = '';
                  }
                }}
                className={css({ display: 'none' })}
              />
              <div
                className={grid({
                  gap: 8,
                  gridTemplateColumns:
                    uploadedFiles.length === 0 ? '1fr' : { base: '1fr 1fr', tablet: `repeat(4, 124px)` },
                })}
                onDrop={(e) => {
                  if (!canUploadMore) return snackbar.error({ message: `You can upload up to ${maxFileCount} files.` });
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.dataTransfer.files) {
                    handleFiles(e.dataTransfer.files);
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {uploadedFiles.map((uploadedFile, index) => (
                  <div
                    key={uploadedFile.preview}
                    className={square({
                      pos: 'relative',
                      aspectRatio: 1,
                      rounded: 8,
                      overflow: 'hidden',
                    })}
                  >
                    <img
                      src={uploadedFile.preview}
                      alt={`Uploaded image #${index + 1}`}
                      className={css({
                        w: '100%',
                        h: '100%',
                        objectFit: 'cover',
                      })}
                    />
                    <IconButton
                      icon={<XCircleIcon />}
                      hierarchy='quaternary'
                      size='medium'
                      onClick={() => removeFile(index)}
                      css={{ pos: 'absolute', top: 0, right: 0 }}
                    />
                  </div>
                ))}
                <div
                  onClick={() => {
                    if (!isLoggedIn) return openLoginDialog();
                    fileInputRef.current?.click();
                  }}
                  className={css(
                    {
                      flexDir: 'column',
                      rounded: 8,
                      bg: 'rgba(255, 255, 255, 0.04)',
                      border: '1px dashed rgba(255, 255, 255, 0.08)',
                      transition: 'background 0.2s ease',
                      cursor: 'pointer',
                      _hover: {
                        bg: 'rgba(255, 208, 132, 0.08)',
                      },
                    },
                    uploadedFiles.length === 0
                      ? vstack.raw({ px: 16, py: 32, gap: 12 })
                      : square.raw({ aspectRatio: 1, gap: 8 }),
                  )}
                >
                  <PlusIcon
                    size={uploadedFiles.length === 0 ? 32 : 20}
                    color={token('colors.content.default.level-5')}
                  />
                  {uploadedFiles.length === 0 ? (
                    <div className={stack({ gap: 4, alignItems: 'center', textAlign: 'center' })}>
                      <p className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-5' })}>
                        <T id='VIP.r260303.Click_or_drag_to_upload' />
                      </p>
                      <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
                        <T
                          id='VIP.r260303.Png_jpg_jpeg_up_to_files_files_max_size_mb_each'
                          values={{ files: maxFileCount, size: maxFileSizeInMB }}
                        />
                      </p>
                    </div>
                  ) : (
                    <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                      {uploadedFiles.length}/{maxFileCount}
                    </p>
                  )}
                </div>
              </div>
              {uploadedFiles.length > 0 && (
                <ul
                  className={css({
                    textStyle: 'Text/S/Regular',
                    color: 'content.default.level-1',
                    '& > li': { listStyle: 'disc', ml: 16 },
                  })}
                >
                  <li>
                    <T
                      id='VIP.r260303.Png_jpg_jpeg_up_to_files_files_max_size_mb_each'
                      values={{ files: maxFileCount, size: maxFileSizeInMB }}
                    />
                  </li>
                </ul>
              )}
              {showUploadError && (
                <p className={css({ textStyle: 'Text/S/Regular', color: 'content.accent.new' })}>
                  <T id='Vip.r260121.Upload_at_least_one_proof' />
                </p>
              )}
            </div>
          </div>

          <div className={stack({ gap: 4 })}>
            <Checkbox
              checked={isAgreed}
              onChange={(e) => {
                if (!isLoggedIn) {
                  openLoginDialog();
                  return;
                }
                setIsAgreed(e.target.checked);
              }}
            >
              <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                <T id='Vip.r260121.I_consent_to_Flipster_collecting_using_and_processing' />
              </span>
            </Checkbox>
            {showConsentError && (
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.accent.new' })}>
                <T id='Vip.r260121.Agree_to_the_consent_to_continue' />
              </p>
            )}
          </div>

          <VipButton
            hierarchy='primary'
            label={isLoggedIn ? <T id='Vip.r260121.Submit' /> : <T id='Vip.r260121.Log_in_to_apply' />}
            css={{ w: '100%' }}
            disabled={isSubmitting}
            onClick={async () => {
              if (!isLoggedIn) return router.push('/login');
              await submitApplication();
            }}
          />
        </GlassCard>

        <GlassCard css={{ p: 24, gap: 16, w: '100%' }}>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
            <T id='Vip.r260121.Eligibility_Next_Steps' />
          </p>
          <div className={stack({ gap: 8 })}>
            <div className={hstack({ gap: 16, py: 8 })}>
              <div
                className={circle({
                  w: 28,
                  h: 28,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'rgba(255, 208, 132, 0.2)',
                  textStyle: 'Text/M/Monospace/Strong',
                  bg: 'rgba(255, 208, 132, 0.1)',
                  color: 'rgba(255, 208, 132)',
                  flexShrink: 0,
                })}
              >
                1
              </div>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                <T id='Vip.r260121.VIP_traders_from_other_exchanges_can_join_Flipster_s_VIP_program' />
              </p>
            </div>
            <div className={hstack({ gap: 16, py: 8 })}>
              <div
                className={circle({
                  w: 28,
                  h: 28,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'rgba(255, 208, 132, 0.2)',
                  textStyle: 'Text/M/Monospace/Strong',
                  bg: 'rgba(255, 208, 132, 0.1)',
                  color: 'rgba(255, 208, 132)',
                  flexShrink: 0,
                })}
              >
                2
              </div>
              <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                <T id='Vip.r260121.Our_VIP_team_will_review_your_application_and_contact_you_via_official_channels' />
              </p>
            </div>
          </div>
        </GlassCard>
      </div>
    </section>
  );
};
